/**
 * Pendo agent via CDN + stub queue — same integration pattern as chargebee-ui
 * (`src/app/utils/analytics/pendo/utils.ts`). Avoids bundling `@pendo/web-sdk`; session replay
 * is handled by Pendo’s hosted agent (worker URL from their config), not the npm Replay plugin.
 */

type PendoQueueItem = unknown[];

interface PendoStub {
  _q: PendoQueueItem[];
  initialize?: (...args: unknown[]) => void;
  identify?: (...args: unknown[]) => void;
  updateOptions?: (...args: unknown[]) => void;
  pageLoad?: (...args: unknown[]) => void;
  isReady?: () => boolean;
}

type WindowWithPendo = Window & { pendo?: PendoStub };

const installKey: { current: string | null } = { current: null };

/**
 * Injects the official Pendo stub and loads `pendo.js` once per page for the given API key.
 * Safe to call multiple times with the same key.
 */
export function ensurePendoSnippet(apiKey: string): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }
  if (installKey.current === apiKey) {
    return;
  }
  if (installKey.current !== null && installKey.current !== apiKey) {
    return;
  }

  const p = window as WindowWithPendo;
  const e = document;
  const n = 'script';

  if (!p.pendo) {
    p.pendo = {} as PendoStub;
  }
  const o = p.pendo;
  o._q = o._q || [];
  const v = ['initialize', 'identify', 'updateOptions', 'pageLoad'] as const;
  for (let w = 0, x = v.length; w < x; w++) {
    const methodName = v[w];
    o[methodName] =
      o[methodName] ||
      function (this: unknown, ...args: unknown[]) {
        const q = o._q;
        const entry: PendoQueueItem = [methodName, ...args];
        if (methodName === v[0]) {
          q.unshift(entry);
        } else {
          q.push(entry);
        }
      };
  }

  const y = e.createElement(n);
  y.async = true;
  y.src = `https://cdn.pendo.io/agent/static/${apiKey}/pendo.js`;
  const z = e.getElementsByTagName(n)[0];
  z?.parentNode?.insertBefore(y, z);

  installKey.current = apiKey;
}

export function pendoInitialize(opts: {
  visitor: Record<string, string | boolean>;
  account?: Record<string, string | null | undefined>;
}): void {
  const w = window as WindowWithPendo;
  w.pendo?.initialize?.(opts);
}

export function pendoIdentify(opts: {
  visitor: Record<string, string | boolean>;
  account?: Record<string, string | null | undefined>;
}): void {
  const w = window as WindowWithPendo;
  w.pendo?.identify?.(opts);
}
